const normalizeText = (text) => (text || "").replace(/^\uFEFF/, "").normalize("NFC").trim();

const segmentAliases = {
  "dubbel med skruv": "double with twist",
  "double with twist": "double with twist"
};

let clipRowsPromise;

export function stripExtension(filename) {
  const nameWithoutExt = (filename || "").replace(/\.[^/.]+$/, "");
  return nameWithoutExt.replace(/_trim$/i, "");
}

function canonicalizeSegment(segment) {
  const normalized = normalizeText(segment).toLowerCase();
  if (/^\d+$/.test(normalized)) {
    return String(parseInt(normalized, 10));
  }

  return segmentAliases[normalized] || normalized;
}

function pathsMatchWithAliases(leftPath, rightPath) {
  const leftSegments = leftPath.split("/").filter(Boolean).map(canonicalizeSegment);
  const rightSegments = rightPath.split("/").filter(Boolean).map(canonicalizeSegment);

  if (leftSegments.length !== rightSegments.length) {
    return false;
  }

  return leftSegments.every((segment, index) => segment === rightSegments[index]);
}

export function normalizeBucketFilePath(path) {
  if (!path || typeof path !== "string") {
    return "";
  }

  const trimmedPath = path.startsWith("/") ? path.slice(1) : path;
  return /\.[^/]+$/.test(trimmedPath) ? trimmedPath : `${trimmedPath}.mp4`;
}

export function removeTrimSuffix(path) {
  return (path || "").replace(/_trim(?=\.[^/.]+$|$)/i, "");
}

function splitPathExtension(path) {
  const normalizedPath = (path || "").startsWith("/") ? path.slice(1) : (path || "");
  const extensionMatch = normalizedPath.match(/(\.[^/.]+)$/);

  if (!extensionMatch) {
    return {
      basePath: normalizedPath,
      extension: ".mp4"
    };
  }

  return {
    basePath: normalizedPath.slice(0, -extensionMatch[1].length),
    extension: extensionMatch[1]
  };
}

export function getClipRows() {
  if (!clipRowsPromise) {
    clipRowsPromise = fetch("/titles.csv")
      .then((response) => response.text())
      .then((text) => text
        .split(/\r?\n/)
        .map((row) => row.trim())
        .filter(Boolean)
        .map((row) => {
          const [path, ...titleParts] = row.split(",");
          return {
            path: normalizeText(path || ""),
            title: normalizeText(titleParts.join(",") || "")
          };
        }))
      .catch(() => []);
  }

  return clipRowsPromise;
}

export function buildVideoPathCandidates(path, canonicalPath = "") {
  const normalizedPath = normalizeBucketFilePath(path);
  if (!normalizedPath) {
    return [];
  }

  const { basePath, extension } = splitPathExtension(normalizedPath);
  const canonicalBasePath = canonicalPath
    ? removeTrimSuffix(splitPathExtension(canonicalPath).basePath)
    : "";
  const candidates = [normalizedPath];

  if (!basePath.endsWith("_trim")) {
    candidates.push(`${basePath}_trim${extension}`);
    candidates.push(`${basePath}_Trim${extension}`);
  }

  if (canonicalBasePath) {
    candidates.push(`${canonicalBasePath}${extension}`);
    candidates.push(`${canonicalBasePath}_trim${extension}`);
    candidates.push(`${canonicalBasePath}_Trim${extension}`);
  }

  return [...new Set(candidates)];
}

export async function resolveCanonicalClipPath({ selectedVideoPath = "", stationTitle = "" }) {
  const clipRows = await getClipRows();
  const clipPaths = clipRows.map((row) => row.path);
  const normalizedSelection = selectedVideoPath
    ? `/${removeTrimSuffix(stripExtension(normalizeText(selectedVideoPath)))}`
    : "";

  if (!clipPaths.length) {
    return "";
  }

  if (normalizedSelection) {
    const exactAliasMatch = clipPaths.find((clipPath) => (
      clipPath === normalizedSelection || pathsMatchWithAliases(clipPath, normalizedSelection)
    ));

    if (exactAliasMatch) {
      return exactAliasMatch;
    }

    const selectedFilename = canonicalizeSegment(normalizedSelection.split("/").pop() || "");
    const filenameMatch = clipPaths.find((clipPath) => (
      canonicalizeSegment(clipPath.split("/").pop() || "") === selectedFilename
    ));

    if (filenameMatch) {
      return filenameMatch;
    }
  }

  const normalizedStationTitle = canonicalizeSegment(removeTrimSuffix(stripExtension(normalizeText(stationTitle))));
  const titleMatch = clipRows.find((row) => (
    canonicalizeSegment(normalizeText(row.title)) === normalizedStationTitle
  ));

  return titleMatch?.path || "";
}
